
import { Options, prop, Vue } from "vue-class-component";
import DetailEstimation from "./detail-estimation.vue";
import Book from "@/app/ui/views/shipment/booking/create-booking/payment-detail/book.vue";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import ModalErrorBalance from "@/app/ui/views/shipment/booking/component/modal-error-balance.vue";
import { BalanceController } from "@/app/ui/controllers/BalanceController";
import { PromoConfigurationListData } from "@/domain/entities/PromoConfiguration";
import { EstimationPrice } from "@/domain/entities/Booking";
import { DetailDiscount } from "@/domain/entities/Tariff";
import formatPriceRM from "@/app/infrastructures/misc/common-library/FormatPriceRM";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import { BookingV3 } from "@/domain/entities/CreateBookingV3";

interface DiscountInterface {
  status: boolean;
  data: PromoConfigurationListData;
}

class Props {
  id = prop<string>({
    default: "",
    type: String
  });
  modelValue = prop<BookingV3>({
    default: new BookingV3(),
    type: Object
  });
  estimationPrice = prop<EstimationPrice>({
    required: true,
    type: EstimationPrice
  });
  isDisabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  form = prop<any>({
    default: {},
    type: Object
  });
  formType = prop<any>({
    default: "",
    type: String
  });
  discountData = prop<DiscountInterface>({
    default: {
      status: false,
      data: Object
    },
    type: Object
  });
  isCodRetail = prop<boolean>({
    default: false,
    type: Boolean
  });
  goodsValue = prop<any>({
    default: "0"
  });
  isLoading = prop<boolean>({
    default: false,
    type: Boolean
  });
  isError = prop<boolean>({
    default: false,
    type: Boolean
  });
  adjustmentRuleTariff = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isEditSttMY = prop<boolean>({
    default: false
  });
  isCustomerApps = prop<boolean>({
    default: false
  });
  clientData = prop<any>({
    default: {},
    type: Object
  });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  emits: ["updateStatusErrorEmbargo", "refresh"],
  components: {
    Book,
    ModalErrorBalance,
    DetailEstimation,
    Skeleton
  }
})
export default class CheckTariffV2 extends Vue.with(Props) {
  isShow = false;
  refs: any = {};

  onOpenTooltipPromo(event: any, index: number) {
    const refs: any = this.$refs;
    refs[`infoPromo${index}`].toggle(event);
  }

  unmounted() {
    BookingController.setDefaultEstimationPrice();
  }

  get isCODOnly() {
    return this.form.isCOD === true && this.form.isDFOD === false;
  }

  isMouseDown = false;
  onFocusTrigger() {
    if (this.isMouseDown) return;

    this.isShow = true;
  }

  get canShowDetail() {
    return (
      this.formType === "manual" && this.dataProfile.account_type === "partner"
    );
  }
  onKeydownTrigger(e: any) {
    if (!this.canShowDetail) return;

    if (e.key === "Escape" || e.key === "ArrowDown") {
      this.isShow = false;
      e.preventDefault();
      return;
    }

    if (e.key === "ArrowUp") {
      this.isShow = true;
      e.preventDefault();
      return;
    }
  }

  async showDetail() {
    if (
      this.formType === "manual" &&
      this.dataProfile.account_type === "partner"
    ) {
      this.isShow = !this.isShow;
    }
  }

  async updateEstimationPrice() {
    this.$emit("refresh");
  }

  get dataDetail() {
    if (!this.isDisabled) {
      return {
        ...this.estimationPrice,
        discountBoards:
          this.estimationPrice.listDiscount.filter(
            (item: DetailDiscount) =>
              item.pdcCategory === "tactical" || item.pdcCategory === "campaign"
          ) || []
      };
    }

    return new EstimationPrice();
  }

  get bigLabelTotalTariff() {
    return this.dataDetail.totalTariff;
  }

  isOpenTooltip = false;
  onOpenTooltip(value: boolean) {
    this.isOpenTooltip = value;
  }

  onCloseAnywhere(value: boolean) {
    this.isShow = value;
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  get maskingTotalCost() {
    if (this.isCrossCountryCod) {
      return `${this.modelValue.destinationCountry.currencySymbol}••••••`;
    }

    return this.accountCurrency === "MYR" ? "RM••••••" : "Rp••••••";
  }

  currency(curr: number) {
    return formatByCountry(curr, {
      type: "money",
      country: AccountController.accountData.countryCode?.toUpperCase(),
      prefix: AccountController.accountData.accountIsForeign ? "RM" : "Rp"
    });
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get balanceData() {
    return BalanceController.balanceData;
  }

  get isErrorEmbargo() {
    return BookingController.isErrorEmbargo;
  }

  showDiscountNominal = false;
  get isDiscountActive() {
    return this.discountData.status || this.dataDetail.isPromo;
  }

  get bookForClientCurrency() {
    const clientCountry = this.clientData?.clientCountry;
    return clientCountry.toLowerCase() === "id" ? "Rp••••••" : "RM••••••";
  }

  get prefixCurrency() {
    if (this.isCrossCountryCod) {
      return `${this.modelValue.destinationCountry.currencySymbol}••••••`;
    }

    if (this.clientData?.id) return this.bookForClientCurrency;
    return this.accountIsNonForeign ? "Rp••••••" : "RM••••••";
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get listDiscount() {
    return this.dataDetail.listDiscount.length > 0
      ? this.dataDetail.listDiscount
          .filter(
            (item: DetailDiscount) =>
              item.pdcCategory === "tactical" || item.pdcCategory === "campaign"
          )
          .map((item: DetailDiscount) => {
            return new PromoConfigurationListData({
              promoConfigurationName: this.$t(
                "bookingV3.shipment.checkTariff.detail.promo.validUntil",
                {
                  name: item.pdcName,
                  date: formatDateWithoutTime(item.pdcEndDate)
                }
              ),
              promoConfigurationDiscountNominal: item.pdcPromoDiscount
            });
          })
      : this.discountData;
  }

  get showLoading() {
    return this.isLoading;
  }

  get showError() {
    return this.isError;
  }

  get adjustmentTariffRuleNonShow() {
    return this.adjustmentRuleTariff.rule.includes(
      this.configValidationForm.NON_SHOW
    );
  }

  get configValidationForm() {
    return ConfigValidationForm;
  }

  get isPosForClient() {
    return (
      this.formType === "client" && this.dataProfile.account_type !== "client"
    );
  }

  get isClientForClient() {
    return (
      this.formType === "client" && this.dataProfile.account_type === "client"
    );
  }
}
